/* eslint-disable max-lines */
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { WizardStep } from '../shared/wizard-stepper';
import { useCreatePlanUnified } from '@utils/api/back-office/plans-bo/use-create-plan-unified';
import { useGetPlanBoDetailed } from '@utils/api/back-office/plans-bo/use-get-plan-bo-detailed';
import { useGetPricePlansBo } from '@utils/api/back-office/price-plans-bo/use-get-price-plans-bo';
import { useGetResellerCustomers } from '@utils/api/back-office/plans-bo/use-get-reseller-customers';
import {
  CreatePlanUnifiedPayload,
  PlanType,
  ProcessingType,
  PricePlanTierInput
} from '@utils/types/back-office/update-plan-bo';
import { PATHS } from '../../../router/paths';
import { WizardFormData } from './create-plan-wizard-types';

interface UseCreatePlanWizardOptions {
  isOpen: boolean;
  onClose: () => void;
  duplicateFromPlanId?: number;
}

export function useCreatePlanWizard({
  onClose,
  duplicateFromPlanId
}: UseCreatePlanWizardOptions) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [resellerSearchQuery, setResellerSearchQuery] = useState('');

  const { mutateAsync: createPlan, isLoading: isCreating } =
    useCreatePlanUnified();
  const { data: pricePlansData } = useGetPricePlansBo('');
  const { data: duplicatePlanData } = useGetPlanBoDetailed(duplicateFromPlanId);
  const { data: resellersData } = useGetResellerCustomers(resellerSearchQuery);

  const resellers = useMemo(() => resellersData?.result || [], [resellersData]);
  const pricePlans = useMemo(
    () => pricePlansData?.result || [],
    [pricePlansData]
  );

  const validationSchema = Yup.object().shape({
    type: Yup.string()
      .oneOf(['Bundle', 'Pay as you Go', 'Perpetual', 'Other'])
      .required(t('type-is-required')),
    sku: Yup.string().required(t('sku-is-required')),
    processing_type: Yup.string()
      .oneOf(['BATCH', 'REALTIME', 'OTHER'])
      .required(t('processing-type-is-required'))
  });

  const {
    register,
    watch,
    setValue,
    control,
    trigger,
    reset,
    formState: { errors }
  } = useForm<WizardFormData>({
    resolver: yupResolver(
      validationSchema
    ) as unknown as Resolver<WizardFormData>,
    mode: 'onChange',
    defaultValues: {
      type: 'Bundle',
      sku: '',
      description: '',
      processing_type: 'BATCH',
      batch_enabled: true,
      realtime_enabled: false,
      realtime_max_concurrent_streams: undefined,
      realtime_languages_csv: '',
      alignment_enabled: false,
      translation_enabled: false,
      templates_exported_enabled: false,
      automatic_training_enabled: false,
      ai_summaries_enabled: false,
      ai_summaries_monthly_limit: undefined,
      stt_pricing_mode: 'existing',
      stt_pricing_tiers: [],
      alignment_pricing_mode: 'existing',
      alignment_pricing_tiers: [],
      translation_pricing_mode: 'flat',
      translation_pricing_tiers: [],
      is_fair_use: false,
      reseller_ids: []
    }
  });

  const watchedValues = watch();
  const planType = watchedValues.type;

  useEffect(() => {
    if (duplicateFromPlanId && duplicatePlanData?.result) {
      const plan = duplicatePlanData.result;

      const sttPlanId = plan.stt_pricing_plan;
      const alignPlanId = plan.alignment_pricing_plan;
      const translationPlanId = plan.translation_pricing_plan;

      reset({
        type: (plan.type as PlanType) || 'Bundle',
        sku: `${plan.sku}-copy`,
        description: plan.description || '',
        processing_type: (plan.processing_type as ProcessingType) || 'BATCH',
        batch_enabled: plan.batch_enabled ?? false,
        realtime_enabled: plan.realtime_enabled ?? false,
        realtime_max_concurrent_streams:
          plan.realtime_max_concurrent_streams ?? undefined,
        realtime_languages_csv: (plan.realtime_languages ?? []).join(', '),
        alignment_enabled: plan.alignment_enabled ?? false,
        translation_enabled: plan.translation_enabled ?? false,
        templates_exported_enabled: plan.templates_exported_enabled ?? false,
        automatic_training_enabled: plan.automatic_training_enabled ?? false,
        ai_summaries_enabled: plan.ai_summaries_enabled ?? false,
        ai_summaries_monthly_limit:
          plan.ai_summaries_monthly_limit ?? undefined,
        stt_pricing_mode: sttPlanId ? 'existing' : 'inline',
        stt_pricing_plan: sttPlanId ?? undefined,
        stt_pricing_tiers: [],
        alignment_pricing_mode: alignPlanId ? 'existing' : 'inline',
        alignment_pricing_plan: alignPlanId ?? undefined,
        alignment_pricing_tiers: [],
        translation_pricing_mode: translationPlanId ? 'existing' : 'flat',
        translation_pricing_plan: translationPlanId ?? undefined,
        translation_pricing_tiers: [],
        stt_limit: plan.stt_limit ?? undefined,
        media_unit_size: plan.media_unit_size ?? undefined,
        translation_cost: plan.translation_cost ?? undefined,
        translation_characters: plan.translation_characters ?? undefined,
        translation_character_limit:
          plan.translation_character_limit ?? undefined,
        monthly_cost: plan.monthly_cost ?? undefined,
        cost: plan.cost ?? undefined,
        minimal_invoice: plan.minimal_invoice ?? undefined,
        automatic_training_unit_cost:
          plan.automatic_training_unit_cost ?? undefined,
        automatic_training_monthly_cost:
          plan.automatic_training_monthly_cost ?? undefined,
        stt_templates_exported_cost:
          plan.stt_templates_exported_cost ?? undefined,
        custom_model_yearly_limit: plan.custom_model_yearly_limit ?? undefined,
        is_fair_use: false,
        reseller_ids: []
      });
    }
  }, [duplicateFromPlanId, duplicatePlanData, reset]);

  const steps: WizardStep[] = [
    { id: 1, label: t('plan-info') },
    { id: 2, label: t('features-pricing') },
    { id: 3, label: t('review') }
  ];

  const totalSteps = steps.length;

  const handleClose = () => {
    reset();
    setCurrentStep(1);
    setResellerSearchQuery('');
    onClose();
  };

  const handleNext = async () => {
    let isValid = true;
    if (currentStep === 1) {
      isValid = await trigger(['type', 'sku', 'processing_type']);
    }
    if (isValid && currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const validateAndNormalizeTiers = (
    tiers: PricePlanTierInput[]
  ): { valid: boolean; error?: string; normalized: PricePlanTierInput[] } => {
    if (tiers.length === 0) return { valid: true, normalized: [] };

    const sortedTiers = [...tiers].sort((a, b) => a.start - b.start);

    if (sortedTiers[0].start !== 0) {
      return {
        valid: false,
        error: t('first-tier-must-start-at-zero'),
        normalized: []
      };
    }

    for (let i = 0; i < sortedTiers.length - 1; i++) {
      const currentTier = sortedTiers[i];
      const nextTier = sortedTiers[i + 1];
      if (currentTier.end === null) {
        return {
          valid: false,
          error: t('only-last-tier-can-be-unlimited'),
          normalized: []
        };
      }
      if (nextTier.start !== currentTier.end + 1) {
        return {
          valid: false,
          error: t('tiers-must-be-consecutive'),
          normalized: []
        };
      }
    }

    const normalizedTiers = sortedTiers.map((tier, index) => ({
      ...tier,
      end: index === sortedTiers.length - 1 ? null : tier.end
    }));

    return { valid: true, normalized: normalizedTiers };
  };

  const handleCreate = async () => {
    try {
      const data = watchedValues;

      const payload: CreatePlanUnifiedPayload = {
        type: data.type,
        sku: data.sku,
        description: data.description || undefined,
        processing_type: data.processing_type,
        batch_enabled: data.batch_enabled,
        realtime_enabled: data.realtime_enabled,
        alignment_enabled: data.alignment_enabled,
        translation_enabled: data.translation_enabled,
        templates_exported_enabled: data.templates_exported_enabled,
        automatic_training_enabled: data.automatic_training_enabled,
        ai_summaries_enabled: data.ai_summaries_enabled
      };

      if (
        data.ai_summaries_enabled &&
        data.ai_summaries_monthly_limit != null
      ) {
        payload.ai_summaries_monthly_limit = Number(
          data.ai_summaries_monthly_limit
        );
      }

      if (data.realtime_enabled) {
        if (
          data.realtime_max_concurrent_streams != null &&
          !isNaN(Number(data.realtime_max_concurrent_streams))
        ) {
          payload.realtime_max_concurrent_streams = Number(
            data.realtime_max_concurrent_streams
          );
        }
        const langs = (data.realtime_languages_csv ?? '')
          .split(',')
          .map(s => s.trim().toLowerCase())
          .filter(Boolean);
        if (langs.length > 0) {
          payload.realtime_languages = Array.from(new Set(langs));
        }
      }

      if (data.batch_enabled || data.realtime_enabled) {
        if (data.stt_pricing_mode === 'existing' && data.stt_pricing_plan) {
          if (
            data.stt_pricing_plan != null &&
            !isNaN(Number(data.stt_pricing_plan))
          ) {
            payload.stt_pricing_plan = Number(data.stt_pricing_plan);
          }
        } else if (
          data.stt_pricing_mode === 'inline' &&
          data.stt_pricing_tiers.length > 0
        ) {
          const sttTierValidation = validateAndNormalizeTiers(
            data.stt_pricing_tiers
          );
          if (!sttTierValidation.valid) {
            toast.error(sttTierValidation.error || t('invalid-stt-tiers'));
            return;
          }
          if (!data.stt_pricing_plan_name?.trim()) {
            toast.error(t('stt-price-plan-name-required'));
            return;
          }
          payload.stt_pricing_tiers = sttTierValidation.normalized;
          payload.stt_pricing_plan_name = data.stt_pricing_plan_name;
        }
        if (data.stt_limit != null && !isNaN(Number(data.stt_limit))) {
          payload.stt_limit = Number(data.stt_limit);
        }
        if (
          data.media_unit_size != null &&
          !isNaN(Number(data.media_unit_size))
        ) {
          payload.media_unit_size = Number(data.media_unit_size);
        }
      }

      if (data.alignment_enabled) {
        if (
          data.alignment_pricing_mode === 'existing' &&
          data.alignment_pricing_plan
        ) {
          if (
            data.alignment_pricing_plan != null &&
            !isNaN(Number(data.alignment_pricing_plan))
          ) {
            payload.alignment_pricing_plan = Number(
              data.alignment_pricing_plan
            );
          }
        } else if (
          data.alignment_pricing_mode === 'inline' &&
          data.alignment_pricing_tiers.length > 0
        ) {
          const alignTierValidation = validateAndNormalizeTiers(
            data.alignment_pricing_tiers
          );
          if (!alignTierValidation.valid) {
            toast.error(
              alignTierValidation.error || t('invalid-alignment-tiers')
            );
            return;
          }
          if (!data.alignment_pricing_plan_name?.trim()) {
            toast.error(t('alignment-price-plan-name-required'));
            return;
          }
          payload.alignment_pricing_tiers = alignTierValidation.normalized;
          payload.alignment_pricing_plan_name =
            data.alignment_pricing_plan_name;
        }
      }

      if (data.translation_enabled) {
        if (
          data.translation_pricing_mode === 'existing' &&
          data.translation_pricing_plan
        ) {
          if (
            data.translation_pricing_plan != null &&
            !isNaN(Number(data.translation_pricing_plan))
          ) {
            payload.translation_pricing_plan = Number(
              data.translation_pricing_plan
            );
          }
        } else if (
          data.translation_pricing_mode === 'inline' &&
          data.translation_pricing_tiers.length > 0
        ) {
          const tTierValidation = validateAndNormalizeTiers(
            data.translation_pricing_tiers
          );
          if (!tTierValidation.valid) {
            toast.error(
              tTierValidation.error || t('invalid-translation-tiers')
            );
            return;
          }
          if (!data.translation_pricing_plan_name?.trim()) {
            toast.error(t('translation-price-plan-name-required'));
            return;
          }
          payload.translation_pricing_tiers = tTierValidation.normalized;
          payload.translation_pricing_plan_name =
            data.translation_pricing_plan_name;
        } else {
          if (
            data.translation_cost != null &&
            !isNaN(Number(data.translation_cost))
          ) {
            payload.translation_cost = Number(data.translation_cost);
          }
          if (
            data.translation_characters != null &&
            !isNaN(Number(data.translation_characters))
          ) {
            payload.translation_characters = Number(
              data.translation_characters
            );
          }
        }
        payload.translation_character_limit =
          data.translation_character_limit != null &&
          !isNaN(Number(data.translation_character_limit))
            ? Number(data.translation_character_limit)
            : null;
      }

      payload.send_exceedance_email = data.send_exceedance_email ?? false;
      payload.exceedance_email_recipients = data.send_exceedance_email
        ? data.exceedance_email_recipients || null
        : null;

      if (
        planType === 'Bundle' &&
        data.monthly_cost != null &&
        !isNaN(Number(data.monthly_cost))
      ) {
        payload.monthly_cost = Number(data.monthly_cost);
      } else if (
        planType === 'Perpetual' &&
        data.cost != null &&
        !isNaN(Number(data.cost))
      ) {
        payload.cost = Number(data.cost);
      } else if (
        planType === 'Pay as you Go' &&
        data.minimal_invoice != null &&
        !isNaN(Number(data.minimal_invoice))
      ) {
        payload.minimal_invoice = Number(data.minimal_invoice);
      }

      if (data.automatic_training_enabled) {
        if (
          data.automatic_training_unit_cost != null &&
          !isNaN(Number(data.automatic_training_unit_cost))
        ) {
          payload.automatic_training_unit_cost = Number(
            data.automatic_training_unit_cost
          );
        }
        if (
          data.automatic_training_monthly_cost != null &&
          !isNaN(Number(data.automatic_training_monthly_cost))
        ) {
          payload.automatic_training_monthly_cost = Number(
            data.automatic_training_monthly_cost
          );
        }
      }

      if (
        data.templates_exported_enabled &&
        data.stt_templates_exported_cost != null &&
        !isNaN(Number(data.stt_templates_exported_cost))
      ) {
        payload.stt_templates_exported_cost = Number(
          data.stt_templates_exported_cost
        );
      }

      if (data.is_fair_use) {
        payload.is_fair_use = true;
        if (
          data.fair_use_rate_limit != null &&
          !isNaN(Number(data.fair_use_rate_limit))
        ) {
          payload.fair_use_rate_limit = Number(data.fair_use_rate_limit);
        }
        if (
          data.automatic_training_unit_limit != null &&
          !isNaN(Number(data.automatic_training_unit_limit))
        ) {
          payload.automatic_training_unit_limit = Number(
            data.automatic_training_unit_limit
          );
        }
        if (
          data.automatic_training_hosting_limit != null &&
          !isNaN(Number(data.automatic_training_hosting_limit))
        ) {
          payload.automatic_training_hosting_limit = Number(
            data.automatic_training_hosting_limit
          );
        }
      }

      if (
        data.custom_model_yearly_limit != null &&
        !isNaN(Number(data.custom_model_yearly_limit))
      ) {
        payload.custom_model_yearly_limit = Number(
          data.custom_model_yearly_limit
        );
      }

      if (data.reseller_ids.length > 0) {
        payload.reseller_ids = data.reseller_ids;
      }

      const result = await createPlan(payload);
      toast.success(t('plan-created-successfully'));
      handleClose();

      if (result?.result?.id) {
        navigate(PATHS(String(result.result.id)).PLAN_DETAILS);
      }
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          data?: {
            detail?: unknown;
            error_description?: string;
            error?: string;
            message?: string;
          };
        };
      };
      const detail = axiosError?.response?.data?.detail;
      let errorMessage: string;

      if (Array.isArray(detail)) {
        errorMessage =
          detail
            .map((d: { msg?: string; loc?: string[] }) => {
              const field = d.loc?.slice(1).join('.') || '';
              return field ? `${field}: ${d.msg}` : d.msg;
            })
            .filter(Boolean)
            .join(', ') || t('failed-to-create-plan');
      } else {
        errorMessage =
          axiosError?.response?.data?.error_description ||
          (typeof detail === 'string' ? detail : null) ||
          axiosError?.response?.data?.error ||
          axiosError?.response?.data?.message ||
          t('failed-to-create-plan');
      }
      toast.error(errorMessage);
    }
  };

  return {
    currentStep,
    setCurrentStep,
    steps,
    totalSteps,
    watchedValues,
    planType,
    register,
    control,
    errors,
    setValue,
    pricePlans,
    resellers,
    resellerSearchQuery,
    setResellerSearchQuery,
    isCreating,
    handleClose,
    handleNext,
    handleBack,
    handleCreate
  };
}
